// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * DOM rendering + the app's outward API. main.ts owns the transport and wiring;
 * this module builds the shell and exposes typed update methods so the rest of
 * the app never touches the DOM directly.
 */
import { icon } from './icons.ts';
import { mountEventDrawer } from './eventlog.ts';
import { openModal, howItWorksHtml, privacyHtml, aboutHtml } from './modals.ts';
import { fmtBytes } from './format.ts';
import { previewText, imageFilename } from './items.ts';
import type { BeamItem, ItemState, Pairing, Status } from './types.ts';

export interface AppCallbacks {
  onCopyLink: () => void;
  onShareLink: () => void;
  onStartScan: () => void;
  onStopScan: () => void;
  onConnectSaved: (id: string) => void;
  onForgetSaved: (id: string) => void;
  onNameChange: (name: string) => void;
  onRememberChange: (on: boolean) => void;
  onBeamText: (text: string) => void;
  onBeamClipboard: () => void;
  onBeamImageFiles: (files: FileList) => void;
  onDisconnect: () => void;
  onCopyItem: (id: string) => void;
  onSaveItem: (id: string) => void;
  onShareItem: (id: string) => void;
  onClearFeed: () => void;
}

const STATUS_TEXT: Record<Status, string> = {
  idle: 'Not connected',
  waiting: 'Waiting for your other device…',
  paired: 'Connected — confirm the safety words',
  ready: 'Connected',
  error: 'Connection problem',
};

const STATE_LABEL: Record<ItemState, string> = {
  sending: 'Sending…',
  sent: 'Sent',
  delivered: 'Delivered',
  received: 'Received',
  error: 'Failed',
};

const KIND_ICON: Record<BeamItem['kind'], string> = {
  text: 'text',
  link: 'link',
  code: 'code',
  image: 'image',
};

export class AppUI {
  private rows = new Map<string, HTMLElement>();
  private cb: AppCallbacks;

  constructor(root: HTMLElement, cb: AppCallbacks) {
    this.cb = cb;
    root.innerHTML = this.shellHtml();
    this.wire();
    mountEventDrawer(document.getElementById('drawer-body') as HTMLElement);
  }

  private shellHtml(): string {
    return `
      <div class="app-shell">
        <header class="topbar">
          <div class="brand">
            <span class="brand-mark">${icon('beam')}</span>
            <span class="brand-name">Beamwell</span>
          </div>
          <nav class="topnav">
            <button type="button" class="nav-btn" id="nav-how">${icon('help')}<span>How it works</span></button>
            <button type="button" class="nav-btn" id="nav-privacy">${icon('shield')}<span>Privacy</span></button>
            <button type="button" class="nav-btn" id="nav-about">${icon('info')}<span>About</span></button>
            <button type="button" class="nav-btn" id="nav-log" aria-label="Event log">${icon('logDrawer')}<span>Log</span></button>
          </nav>
        </header>

        <button type="button" class="trust-banner" id="trust-banner">
          ${icon('lock')}
          <span>Runs entirely in your browser. Everything you beam travels <strong>directly between your devices</strong>, encrypted end-to-end — never uploaded.</span>
        </button>

        <main class="main-content">
          <section class="card pairing-card" id="pairing-card">
            <h1 class="card-title">Beam it to your other device</h1>
            <p class="card-sub">Pair your phone and laptop once — scan the QR or open the link on the other device — then send text, links, codes and images between them instantly.</p>

            <div class="saved-wrap" id="saved-wrap" hidden>
              <div class="section-label">Your paired devices</div>
              <div class="saved-list" id="saved-list"></div>
              <div class="or-rule"><span>or pair a new device</span></div>
            </div>

            <div class="pair-grid">
              <div class="pair-left">
                <label class="field-label" for="link-input">Pairing link</label>
                <div class="link-row">
                  <input type="text" id="link-input" class="link-input" readonly />
                  <button type="button" class="btn btn-ghost" id="btn-copy" title="Copy link">${icon('copy')}</button>
                  <button type="button" class="btn btn-ghost" id="btn-share" title="Share link">${icon('share')}</button>
                </div>
                <button type="button" class="btn btn-scan" id="btn-scan" hidden>${icon('camera')}<span>Scan the QR on my other device</span></button>
                <div class="opts-row">
                  <label class="field-label" for="name-input">This device’s name</label>
                  <input type="text" id="name-input" class="text-input" maxlength="40" placeholder="e.g. Laptop" />
                </div>
                <label class="remember-row">
                  <input type="checkbox" id="remember-check" />
                  <span>Remember this pairing for one-tap reconnect on this device</span>
                </label>
              </div>
              <div class="pair-right">
                <div class="qr-box" id="qr-box"></div>
                <span class="qr-cap">${icon('qr')} Scan on your other device</span>
              </div>
            </div>

            <div class="scan-panel" id="scan-panel" hidden>
              <div class="scan-frame">
                <video id="scan-video" playsinline muted></video>
                <div class="scan-reticle"></div>
              </div>
              <div class="scan-foot">
                <span class="muted small">Point the camera at the QR on your other device.</span>
                <button type="button" class="btn btn-ghost btn-sm" id="btn-scan-cancel">${icon('x')}<span>Cancel</span></button>
              </div>
            </div>
          </section>

          <section class="card session-card" id="session-card" hidden>
            <div class="status-row">
              <span class="status-dot" id="status-dot" data-status="idle"></span>
              <span class="status-text" id="status-text">${STATUS_TEXT.idle}</span>
              <span class="peer-name" id="peer-name"></span>
              <button type="button" class="btn btn-danger btn-sm" id="btn-disconnect">${icon('power')}<span>Disconnect</span></button>
            </div>

            <div class="sas-box" id="sas-box" hidden>
              <div class="sas-head">${icon('shield')} Safety words — read aloud, they must match on both devices</div>
              <div class="sas-words" id="sas-words"></div>
            </div>

            <div class="extra-peer" id="extra-peer" hidden>${icon('users')} Another device tried to join. Beamwell pairs two devices only.</div>

            <div class="compose" id="compose-zone">
              <textarea id="compose" class="compose-input" rows="3" placeholder="Type or paste text, a link, or a one-time code — then Beam it across."></textarea>
              <div class="staged" id="staged" hidden>
                <img id="staged-thumb" alt="Staged image preview" />
                <span class="staged-name" id="staged-name"></span>
                <button type="button" class="btn btn-ghost btn-sm" id="staged-clear" title="Remove image">${icon('x')}</button>
              </div>
              <div class="compose-actions">
                <button type="button" class="btn btn-ghost btn-sm" id="btn-clip">${icon('clipboard')}<span>Beam clipboard</span></button>
                <button type="button" class="btn btn-ghost btn-sm" id="btn-image">${icon('image')}<span>Image</span></button>
                <input type="file" id="image-input" accept="image/*" hidden />
                <button type="button" class="btn btn-primary" id="btn-beam">${icon('send')}<span>Beam</span></button>
              </div>
              <div class="compose-hint muted small">Drop an image here, or press <kbd>${cmdKey()}</kbd>+<kbd>Enter</kbd> to beam.</div>
            </div>

            <div class="feed-wrap">
              <div class="feed-head">
                <span class="feed-title">Beamed items</span>
                <button type="button" class="filter-pill" id="btn-clear-feed">clear</button>
              </div>
              <div class="feed" id="feed"><div class="feed-empty" id="feed-empty">Nothing beamed yet. Send something across.</div></div>
            </div>
          </section>
        </main>

        <footer class="site-footer">
          <div class="footer-inner">
            Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
            · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
          </div>
        </footer>

        <aside class="drawer" id="drawer" hidden aria-label="Event log">
          <div id="drawer-body"></div>
        </aside>
      </div>`;
  }

  private wire(): void {
    const $ = (id: string) => document.getElementById(id) as HTMLElement;
    $('nav-how').addEventListener('click', () => openModal('How Beamwell works', howItWorksHtml()));
    $('nav-privacy').addEventListener('click', () => openModal('Privacy & threat model', privacyHtml()));
    $('nav-about').addEventListener('click', () => openModal('About Beamwell', aboutHtml()));
    $('trust-banner').addEventListener('click', () => openModal('Privacy & threat model', privacyHtml()));
    $('nav-log').addEventListener('click', () => this.toggleDrawer());

    $('btn-copy').addEventListener('click', () => this.cb.onCopyLink());
    $('btn-share').addEventListener('click', () => this.cb.onShareLink());
    $('btn-scan').addEventListener('click', () => this.cb.onStartScan());
    $('btn-scan-cancel').addEventListener('click', () => this.cb.onStopScan());
    $('btn-disconnect').addEventListener('click', () => this.cb.onDisconnect());
    $('btn-clear-feed').addEventListener('click', () => this.cb.onClearFeed());

    const name = document.getElementById('name-input') as HTMLInputElement;
    name.addEventListener('change', () => this.cb.onNameChange(name.value.trim()));
    const remember = document.getElementById('remember-check') as HTMLInputElement;
    remember.addEventListener('change', () => this.cb.onRememberChange(remember.checked));

    // Saved-pairing list (delegated).
    $('saved-list').addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      const forget = t.closest('[data-forget]') as HTMLElement | null;
      if (forget) {
        this.cb.onForgetSaved(forget.dataset.forget as string);
        return;
      }
      const card = t.closest('[data-connect]') as HTMLElement | null;
      if (card) this.cb.onConnectSaved(card.dataset.connect as string);
    });

    // Compose.
    const compose = document.getElementById('compose') as HTMLTextAreaElement;
    $('btn-beam').addEventListener('click', () => this.cb.onBeamText(compose.value));
    compose.addEventListener('keydown', (e) => {
      const ke = e as KeyboardEvent;
      if ((ke.metaKey || ke.ctrlKey) && ke.key === 'Enter') {
        e.preventDefault();
        this.cb.onBeamText(compose.value);
      }
    });
    $('btn-clip').addEventListener('click', () => this.cb.onBeamClipboard());
    $('btn-image').addEventListener('click', () => (document.getElementById('image-input') as HTMLInputElement).click());
    const imageInput = document.getElementById('image-input') as HTMLInputElement;
    imageInput.addEventListener('change', () => {
      if (imageInput.files && imageInput.files.length) this.cb.onBeamImageFiles(imageInput.files);
      imageInput.value = '';
    });
    $('staged-clear').addEventListener('click', () => this.setStaged(null));

    // Image drop onto the compose zone.
    const zone = $('compose-zone');
    zone.addEventListener('dragover', (e) => {
      e.preventDefault();
      zone.classList.add('drag');
    });
    zone.addEventListener('dragleave', () => zone.classList.remove('drag'));
    zone.addEventListener('drop', (e) => {
      e.preventDefault();
      zone.classList.remove('drag');
      const files = (e as DragEvent).dataTransfer?.files;
      if (files && files.length) this.cb.onBeamImageFiles(files);
    });

    // Feed row actions (delegated).
    $('feed').addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      const btn = t.closest('[data-act]') as HTMLElement | null;
      if (!btn) return;
      const id = btn.dataset.id as string;
      switch (btn.dataset.act) {
        case 'copy': this.cb.onCopyItem(id); break;
        case 'save': this.cb.onSaveItem(id); break;
        case 'share': this.cb.onShareItem(id); break;
      }
    });

    // Drawer close (× and Escape).
    document.getElementById('drawer')?.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('#drawer-close')) this.closeDrawer();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeDrawer();
    });
  }

  private toggleDrawer(): void {
    const d = document.getElementById('drawer') as HTMLElement;
    if (d.hidden) {
      d.hidden = false;
      d.classList.add('open');
    } else {
      this.closeDrawer();
    }
  }
  private closeDrawer(): void {
    const d = document.getElementById('drawer') as HTMLElement;
    d.classList.remove('open');
    d.hidden = true;
  }

  // ── public update API ───────────────────────────────────────────────────
  setSupportsScan(ok: boolean): void {
    (document.getElementById('btn-scan') as HTMLElement).hidden = !ok;
  }

  setInviteLink(url: string): void {
    (document.getElementById('link-input') as HTMLInputElement).value = url;
  }

  setQr(dataUrl: string): void {
    const box = document.getElementById('qr-box') as HTMLElement;
    box.innerHTML = `<img src="${dataUrl}" alt="Pairing QR code" width="160" height="160" />`;
  }

  setName(name: string): void {
    (document.getElementById('name-input') as HTMLInputElement).value = name;
  }

  setRemember(on: boolean): void {
    (document.getElementById('remember-check') as HTMLInputElement).checked = on;
  }

  setSas(words: string[]): void {
    const box = document.getElementById('sas-box') as HTMLElement;
    const wrap = document.getElementById('sas-words') as HTMLElement;
    wrap.innerHTML = words.map(() => `<span class="sas-word"></span>`).join('');
    wrap.querySelectorAll('.sas-word').forEach((el, i) => (el.textContent = words[i]));
    box.hidden = false;
  }

  renderSaved(pairings: Pairing[], activeRoom: string): void {
    const wrap = document.getElementById('saved-wrap') as HTMLElement;
    const list = document.getElementById('saved-list') as HTMLElement;
    const visible = pairings.filter((p) => p.room !== activeRoom);
    if (!visible.length) {
      wrap.hidden = true;
      list.innerHTML = '';
      return;
    }
    wrap.hidden = false;
    list.innerHTML = visible
      .map(
        (p) => `
      <div class="saved-card" data-connect="${p.id}" role="button" tabindex="0">
        <span class="saved-icon">${icon('devices')}</span>
        <span class="saved-name"></span>
        <button type="button" class="saved-forget" data-forget="${p.id}" title="Forget this pairing">${icon('trash')}</button>
      </div>`,
      )
      .join('');
    list.querySelectorAll('.saved-card').forEach((el, i) => {
      (el.querySelector('.saved-name') as HTMLElement).textContent = visible[i].name;
    });
  }

  showPairing(show: boolean): void {
    (document.getElementById('pairing-card') as HTMLElement).hidden = !show;
  }
  showSession(show: boolean): void {
    (document.getElementById('session-card') as HTMLElement).hidden = !show;
  }

  scanVideo(): HTMLVideoElement {
    return document.getElementById('scan-video') as HTMLVideoElement;
  }
  setScanning(on: boolean): void {
    (document.getElementById('scan-panel') as HTMLElement).hidden = !on;
  }

  setStatus(status: Status, peerName?: string): void {
    (document.getElementById('status-dot') as HTMLElement).dataset.status = status;
    (document.getElementById('status-text') as HTMLElement).textContent = STATUS_TEXT[status];
    (document.getElementById('peer-name') as HTMLElement).textContent = peerName ? `· ${peerName}` : '';
  }

  setPeer(name: string | null): void {
    (document.getElementById('peer-name') as HTMLElement).textContent = name ? `· ${name}` : '';
  }

  showExtraPeer(show: boolean): void {
    (document.getElementById('extra-peer') as HTMLElement).hidden = !show;
  }

  getCompose(): string {
    return (document.getElementById('compose') as HTMLTextAreaElement).value;
  }
  setCompose(v: string): void {
    (document.getElementById('compose') as HTMLTextAreaElement).value = v;
  }
  focusCompose(): void {
    (document.getElementById('compose') as HTMLTextAreaElement).focus();
  }

  /** Show/clear a staged image about to be beamed. */
  setStaged(preview: { url: string; name: string } | null): void {
    const el = document.getElementById('staged') as HTMLElement;
    if (!preview) {
      el.hidden = true;
      (document.getElementById('staged-thumb') as HTMLImageElement).removeAttribute('src');
      return;
    }
    (document.getElementById('staged-thumb') as HTMLImageElement).src = preview.url;
    (document.getElementById('staged-name') as HTMLElement).textContent = preview.name;
    el.hidden = false;
  }

  clearFeed(): void {
    const feed = document.getElementById('feed') as HTMLElement;
    feed.innerHTML = `<div class="feed-empty" id="feed-empty">Nothing beamed yet. Send something across.</div>`;
    this.rows.clear();
  }

  /** Insert or update a feed row for an item. */
  upsertItem(item: BeamItem): void {
    const feed = document.getElementById('feed') as HTMLElement;
    document.getElementById('feed-empty')?.remove();
    let el = this.rows.get(item.id);
    if (!el) {
      el = document.createElement('div');
      el.className = 'feed-row';
      feed.prepend(el);
      this.rows.set(item.id, el);
    }
    el.dataset.state = item.state;
    el.dataset.dir = item.dir;
    el.innerHTML = this.itemHtml(item);
    // Fill text nodes safely (avoid HTML injection from item content).
    const body = el.querySelector('.fi-body') as HTMLElement | null;
    if (body && item.kind !== 'image') body.textContent = previewText(item.text ?? '');
    const nameEl = el.querySelector('.fi-imgname') as HTMLElement | null;
    if (nameEl) nameEl.textContent = imageFilename(item.name, item.mime, item.at);
    const thumb = el.querySelector('.fi-thumb') as HTMLImageElement | null;
    if (thumb && item.blobUrl) thumb.src = item.blobUrl;
  }

  private itemHtml(item: BeamItem): string {
    const dirIcon = icon(item.dir === 'in' ? 'arrowIn' : 'arrowOut');
    const kindIcon = icon(KIND_ICON[item.kind]);
    const state = item.state === 'delivered' ? icon('checks') : item.state === 'sent' ? icon('check') : '';
    const stateLabel = STATE_LABEL[item.state];

    let mid = '';
    if (item.kind === 'image') {
      mid = `<div class="fi-image"><img class="fi-thumb" alt="Beamed image" />${
        item.size ? `<span class="fi-size">${fmtBytes(item.size)}</span>` : ''
      }<span class="fi-imgname"></span></div>`;
    } else {
      mid = `<div class="fi-body"></div>`;
    }

    const actions: string[] = [];
    if (item.kind === 'image') {
      if (item.bytes) {
        actions.push(this.actBtn('save', item.id, 'download', 'Save'));
        actions.push(this.actBtn('share', item.id, 'share', 'Share'));
      }
    } else {
      actions.push(this.actBtn('copy', item.id, 'copy', 'Copy'));
      if (item.kind === 'link' && item.text) {
        actions.push(
          `<a class="fi-act" href="${encodeURI(item.text)}" target="_blank" rel="noopener noreferrer" title="Open">${icon('external')}</a>`,
        );
      }
    }

    return `
      <span class="fi-dir">${dirIcon}</span>
      <span class="fi-kind">${kindIcon}</span>
      ${mid}
      <div class="fi-meta">
        <span class="fi-state">${state}${stateLabel}</span>
      </div>
      <div class="fi-actions">${actions.join('')}</div>`;
  }

  private actBtn(act: string, id: string, ic: string, title: string): string {
    return `<button type="button" class="fi-act" data-act="${act}" data-id="${id}" title="${title}" aria-label="${title}">${icon(ic)}</button>`;
  }
}

function cmdKey(): string {
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  return isMac ? '⌘' : 'Ctrl';
}
